'use client';

import { useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { testimonials, type Testimonial } from '@/data/testimonials';

export function TestimonialsSection({ reviews }: { reviews?: Testimonial[] }) {
  const t = useTranslations('public.testimonials');
  const scroller = useRef<HTMLDivElement>(null);

  // Featured verified reviews replace the static testimonials when present;
  // the static array is the empty-state fallback only (no regression).
  const items = reviews && reviews.length > 0 ? reviews : testimonials;

  const page = (dir: 1 | -1) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth, behavior: 'smooth' });
  };

  return (
    <section className="bg-[var(--eifo-bg-light)] py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4">
        <div className="mb-8 flex items-end justify-between gap-4">
          <h2 className="font-display flex-1 text-center text-3xl font-bold text-eifo-primary md:text-4xl">
            {t('heading')}
          </h2>
          <div className="hidden shrink-0 gap-2 sm:flex">
            <button
              type="button"
              onClick={() => page(-1)}
              aria-label={t('previous')}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-eifo-orange/30 bg-white text-eifo-orange transition-colors hover:bg-eifo-orange hover:text-white"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => page(1)}
              aria-label={t('next')}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-eifo-orange/30 bg-white text-eifo-orange transition-colors hover:bg-eifo-orange hover:text-white"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div
          ref={scroller}
          className="flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {items.map((t) => (
            <div
              key={t.id}
              className="shrink-0 snap-start basis-full sm:basis-[calc(50%-12px)] lg:basis-[calc(33.333%-16px)]"
            >
              <div className="relative h-full rounded-xl border bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                <span className="absolute -top-3 left-6 text-5xl text-eifo-orange/20">&ldquo;</span>
                <div className="mb-3 flex gap-0.5">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-eifo-orange text-eifo-orange" />
                  ))}
                </div>
                <p className="mb-4 line-clamp-[10] text-sm italic text-gray-600">{t.quote}</p>
                <p className="text-sm font-semibold text-eifo-primary">{t.attribution}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
